/**
 * Precision monetaria compartida por el PWA.
 *
 * Sin proveedor se usa el fallback de dos decimales, igual que el backend.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tenantIdOrNull, tenantScoped } from '@/config/queryClient';
import { monetaryPrecisionService } from '@/services/monetaryPrecisionService';
import { useMobileAuth } from '@/state/MobileAuthContext';
import {
  createMonetaryPrecision,
  DEFAULT_MONETARY_PRECISION,
  type MonetaryPrecision,
} from '@/utils/monetaryPrecision';

const MonetaryPrecisionContext = createContext<MonetaryPrecision>(
  DEFAULT_MONETARY_PRECISION,
);

export function MonetaryPrecisionProvider({ children }: { children: ReactNode }) {
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  const { data } = useQuery({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'monetary-precision')
        : ['tenant', 'no-context', 'monetary-precision'],
    queryFn: monetaryPrecisionService.get,
    enabled: tenantId !== null,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const value = useMemo(() => createMonetaryPrecision(data), [data]);
  return (
    <MonetaryPrecisionContext.Provider value={value}>
      {children}
    </MonetaryPrecisionContext.Provider>
  );
}

export function useMonetaryPrecision(): MonetaryPrecision {
  return useContext(MonetaryPrecisionContext);
}
